import type { DbExecutor } from "./executor";
import type { BomService } from "./bomService";
import type { CatalogRepository } from "./repositories/catalogRepository";
import type { PurchaseRepository } from "./repositories/purchaseRepository";
import type { AssemblyRepository } from "./repositories/assemblyRepository";
import { ValidationError } from "./repositories/projectRepository";
import { computeBom, type BomGroup } from "../engine/bom";
import { computePurchaseQuantity } from "../engine/purchaseQuantity";
import {
  computeFulfillment,
  type FulfillmentResult,
  type ProcurementDisposition,
  type PurchaseEvidenceInput,
} from "../engine/procurement";
import type { PriceObservation, Product } from "../domain/catalog";
import { effectiveSourcingZip } from "../domain/project";
import type { Cents } from "../domain/money";
import type { QuantityUnit } from "../domain/feature";

/**
 * Procurement service (Phase 9B, spec §26A–C): the read-model and workflow
 * glue for "what do I need to buy, how much, where, at what cost, and have
 * I bought it?".
 *
 * Hard rules preserved:
 *  - fulfillment is DERIVED from the existing Purchase → Line → Allocation
 *    accounting (product-linked lines only — unlinked lines are never
 *    guessed into a requirement);
 *  - the only persisted procurement state is the user's disposition
 *    (crew-supplied / not-required);
 *  - Record Purchase writes ONLY the existing Phase 5 records — there is
 *    no second "purchased materials" ledger;
 *  - selecting/updating offers goes through the existing copy-on-use
 *    selection snapshots — current offers never silently reprice a project.
 */

export interface ProcurementRow {
  /** productId for selected requirements, else `unselected:<componentId>`. */
  key: string;
  productId: string | null;
  productName: string;
  supplierName: string;
  materialDefinitionId: string | null;
  componentIds: string[];
  featureIds: string[];
  /** Distinct room/floor/project labels contributing demand. */
  scopeLabels: string[];
  /** Construction unit (sqft, each, …). */
  unit: string;
  purchaseUnit: string | null;
  /** Total construction requirement (×100). */
  requiredX100: number;
  /** Whole purchase units required (Σ effective units of the BOM groups). */
  requiredUnits: number;
  purchasedCoverageX100: number;
  packageExcessX100: number;
  priceCentsSnapshot: Cents | null;
  priceSource: string | null;
  priceObservedAt: string | null;
  /** Estimated extended cost from the BOM (incl. estimating tax). */
  estimatedTotalCents: Cents;
  needsSelection: boolean;
  fulfillment: FulfillmentResult;
  disposition: ProcurementDisposition | null;
  /** True when member components disagree (surfaced, never auto-resolved). */
  dispositionMixed: boolean;
  /** Requirement explanation lines (feature → component → quantity). */
  demandExplanations: string[];
  /** Snapshot-vs-current offer comparison; null when nothing newer differs. */
  currentOffer: OfferComparison | null;
  /** Frozen requirement variants; fulfillment/evidence remains one container per product. */
  snapshotGroups?: BomGroup[];
  /** Incompatible purchase or construction units cannot be added or fulfilled by assumption. */
  quantityReviewRequired?: boolean;
}

export interface OfferComparison {
  observation: PriceObservation;
  snapshotCents: Cents;
  diffPerUnitCents: Cents;
  extendedDiffCents: Cents;
}

export interface ProductOption {
  product: Product;
  latestOffer: PriceObservation | null;
  /** Whole purchase units needed to cover the requirement. */
  purchaseUnits: number;
  purchasedCoverageX100: number;
  extendedCostCents: Cents | null;
  isCurrentSelection: boolean;
}

export interface RecordPurchaseInput {
  projectId: string;
  productId: string;
  /** Requirement components (for the feature link when unambiguous). */
  componentIds: string[];
  vendor: string;
  date: string;
  receiptNumber: string;
  /** Purchase units bought, ×100. */
  quantityX100: number;
  unit: string;
  merchandiseCents: Cents;
  discountCents: Cents;
  taxCents: Cents;
  shippingCents: Cents;
  notes: string;
}

interface EvidenceRow {
  purchase_id: string;
  line_id: string;
  alloc_id: string;
  vendor: string;
  date: string;
  kind: string;
  product_id: string;
  line_qty_x100: number | null;
  unit: string | null;
  alloc_qty_x100: number | null;
  amount_cents: number;
  alloc_count: number;
}

export class ProcurementService {
  private privateCompanyPurchaseIds:ReadonlySet<string>=new Set();
  /** Request-local collaborator boundary; owner instances keep ordinary evidence. */
  setPrivateCompanyPurchaseIds(ids:ReadonlySet<string>){this.privateCompanyPurchaseIds=ids;}
  private selectionHook: ((projectId: string, componentIds: string[], productId: string, observationId: string | null) => Promise<void>) | null = null;
  /** Hosted selection and regeneration must share one server transaction. */
  setSelectionHook(hook: NonNullable<ProcurementService["selectionHook"]>): void { this.selectionHook = hook; }
  constructor(
    private readonly db: DbExecutor,
    private readonly bom: BomService,
    private readonly catalog: CatalogRepository,
    private readonly purchases: PurchaseRepository,
    private readonly assemblies: AssemblyRepository,
  ) {}

  /** The project's sourcing market: explicit override else address ZIP. */
  async sourcingContext(projectId: string): Promise<{ zip: string; isOverride: boolean }> {
    const rows = await this.db.select<{ zip: string; sourcing_zip: string | null }>(
      "SELECT zip, sourcing_zip FROM projects WHERE id = ?",
      [projectId],
    );
    if (rows.length === 0) return { zip: "", isOverride: false };
    const r = rows[0];
    const zip = effectiveSourcingZip({ zip: r.zip ?? "", sourcingZip: r.sourcing_zip ?? null });
    return { zip, isOverride: (r.sourcing_zip ?? "").trim() !== "" };
  }

  /** Product-linked purchase evidence for the project, grouped by product. */
  private async evidenceByProduct(projectId: string): Promise<Map<string, PurchaseEvidenceInput[]>> {
    const rows = await this.db.select<EvidenceRow>(
      `SELECT p.id AS purchase_id, l.id AS line_id, a.id AS alloc_id, p.vendor, p.date, p.kind,
              l.product_id, l.quantity_x100 AS line_qty_x100, l.unit,
              a.quantity_x100 AS alloc_qty_x100, a.amount_cents,
              (SELECT COUNT(*) FROM purchase_allocations a2 WHERE a2.line_id = l.id) AS alloc_count
         FROM purchase_allocations a
         JOIN purchase_lines l ON l.id = a.line_id
         JOIN purchases p ON p.id = l.purchase_id
        WHERE a.project_id = ? AND l.product_id IS NOT NULL AND p.status = 'active'
        ORDER BY p.date, p.created_at`,
      [projectId],
    );
    const map = new Map<string, PurchaseEvidenceInput[]>();
    for (const r of rows) {
      if(this.privateCompanyPurchaseIds.has(r.purchase_id)){
        r.vendor="Project portion only — business source is private";
        r.line_qty_x100=null;
        r.alloc_count=0;
      }
      const arr = map.get(r.product_id) ?? [];
      arr.push({
        purchaseId: r.purchase_id,
        lineId: r.line_id,
        allocationId: r.alloc_id,
        vendor: r.vendor,
        date: r.date,
        kind: r.kind,
        allocationQtyX100: r.alloc_qty_x100 === null ? null : Number(r.alloc_qty_x100),
        lineQtyX100: r.line_qty_x100 === null ? null : Number(r.line_qty_x100),
        lineAllocationCount: Number(r.alloc_count),
        allocatedCents: Number(r.amount_cents),
        unit: r.unit,
      });
      map.set(r.product_id, arr);
    }
    return map;
  }

  private async dispositions(componentIds: string[]): Promise<Map<string, ProcurementDisposition>> {
    if (componentIds.length === 0) return new Map();
    const placeholders = componentIds.map(() => "?").join(",");
    const rows = await this.db.select<{ component_id: string; disposition: string }>(
      `SELECT component_id, disposition FROM procurement_dispositions WHERE component_id IN (${placeholders})`,
      componentIds,
    );
    return new Map(rows.map((r) => [r.component_id, r.disposition as ProcurementDisposition]));
  }

  /** Material definition ids for components (for alternatives). */
  private async materialDefinitionsFor(componentIds: string[]): Promise<Map<string, string | null>> {
    if (componentIds.length === 0) return new Map();
    const placeholders = componentIds.map(() => "?").join(",");
    const rows = await this.db.select<{ id: string; material_definition_id: string | null }>(
      `SELECT id, material_definition_id FROM assembly_instance_components WHERE id IN (${placeholders})`,
      componentIds,
    );
    return new Map(rows.map((r) => [r.id, r.material_definition_id ?? null]));
  }

  /**
   * The project-wide procurement view for the ACTIVE design: one row per
   * required product (unselected requirements stay their own rows).
   * Requirements come from the same BOM the estimate uses; fulfillment
   * derives from the same accounting the books use.
   */
  async forProject(projectId: string): Promise<ProcurementRow[]> {
    const demands = await this.bom.demandsForProject(projectId);
    const taxRows = await this.db.select<{ estimating_tax_pct_x100: number | null }>(
      "SELECT estimating_tax_pct_x100 FROM projects WHERE id = ?",
      [projectId],
    );
    const bom = computeBom(demands, taxRows[0]?.estimating_tax_pct_x100 ?? null);
    const selectionByComponent = new Map(demands.filter((d) => d.selection).map((d) => [d.componentId, d.selection!]));

    // Merge BOM groups by product so purchase evidence is never counted
    // twice (round-separately splits share one product's purchases).
    const merged = new Map<string, BomGroup[]>();
    for (const g of bom.groups) {
      const key = g.productId ?? g.key; // unselected groups stay solo
      const arr = merged.get(key) ?? [];
      arr.push(g);
      merged.set(key, arr);
    }

    const allComponentIds = bom.groups.flatMap((g) => g.components.map((c) => c.componentId));
    const [evidence, dispositionMap, defMap, sourcing] = await Promise.all([
      this.evidenceByProduct(projectId),
      this.dispositions(allComponentIds),
      this.materialDefinitionsFor(allComponentIds),
      this.sourcingContext(projectId),
    ]);

    const rows: ProcurementRow[] = [];
    for (const [key, groups] of merged) {
      const productId = groups[0].productId;
      const componentIds = groups.flatMap((g) => g.components.map((c) => c.componentId));
      const featureIds = [...new Set(groups.flatMap((g) => g.components.map((c) => c.featureId)))];
      const scopeLabels = [...new Set(groups.flatMap((g) => g.components.map((c) => c.scopeLabel)))];
      const bases = new Set(groups.map(group => JSON.stringify([group.purchaseUnit, group.unit, group.coveragePerUnitX100, group.priceCentsSnapshot, group.priceIncludesTax])));
      const mixedSnapshots = bases.size > 1;
      const quantityReviewRequired = new Set(groups.map(group => group.purchaseUnit)).size > 1 || new Set(groups.map(group => group.unit)).size > 1;
      const requiredUnits = quantityReviewRequired ? 0 : groups.reduce((s, g) => s + g.effectivePurchaseUnits, 0);
      const requiredX100 = quantityReviewRequired ? 0 : groups.reduce((s, g) => s + g.requiredX100, 0);
      const needsSelection = groups.every((g) => g.needsSelection);

      const memberDispositions = componentIds.map((id) => dispositionMap.get(id) ?? null);
      const first = memberDispositions[0] ?? null;
      const allSame = memberDispositions.every((d) => d === first);
      const disposition = allSame ? first : null;
      const dispositionMixed = !allSame;

      const productEvidence = productId ? (evidence.get(productId) ?? []) : [];
      const incompatibleEvidence = (item: PurchaseEvidenceInput) => quantityReviewRequired || item.unit !== null && item.unit !== groups[0].purchaseUnit;
      const fulfillment = computeFulfillment({
        requiredUnits,
        hasProductSelection: !needsSelection,
        disposition,
        evidence: productEvidence.map(item => incompatibleEvidence(item) ? { ...item, allocationQtyX100: null, lineQtyX100: null } : item),
      });
      // Keep recorded receipt quantities intact for provenance. Only their
      // attribution is unknown; never say a recorded quantity was absent.
      fulfillment.evidence = fulfillment.evidence.map((item, index) => incompatibleEvidence(productEvidence[index]) ? { ...item, allocationQtyX100: productEvidence[index].allocationQtyX100, lineQtyX100: productEvidence[index].lineQtyX100, explanation: "Recorded purchase units cannot be compared with these requirement snapshots without an explicit unit review. Merchandise counted once." } : item);

      const snapshotSelection = componentIds.map((id) => selectionByComponent.get(id)).find((s) => s) ?? null;
      const currentOffer = productId && snapshotSelection && !mixedSnapshots
        ? await this.currentOfferComparison(productId, snapshotSelection.priceCentsSnapshot, snapshotSelection.priceSourceSnapshot, snapshotSelection.priceObservedAtSnapshot, requiredUnits, sourcing.zip)
        : null;

      rows.push({
        key,
        productId,
        productName: groups[0].productName,
        supplierName: groups[0].supplierName,
        materialDefinitionId: defMap.get(componentIds[0]) ?? null,
        componentIds,
        featureIds,
        scopeLabels,
        unit: groups[0].unit,
        purchaseUnit: quantityReviewRequired ? null : groups[0].purchaseUnit,
        requiredX100,
        requiredUnits,
        purchasedCoverageX100: groups.reduce((s, g) => s + g.purchasedCoverageX100, 0),
        packageExcessX100: groups.reduce((s, g) => s + g.roundingExcessX100, 0),
        priceCentsSnapshot: mixedSnapshots ? null : groups[0].priceCentsSnapshot,
        priceSource: mixedSnapshots ? null : groups[0].priceSource,
        priceObservedAt: snapshotSelection?.priceObservedAtSnapshot ?? null,
        estimatedTotalCents: groups.reduce((s, g) => s + g.totalCents, 0),
        needsSelection,
        fulfillment,
        disposition,
        dispositionMixed,
        demandExplanations: groups.flatMap((g) =>
          g.components.map((c) => `${c.scopeLabel} → ${c.featureName} → ${c.componentName}: ${(c.requiredX100 / 100).toLocaleString()} ${g.unit}`),
        ),
        currentOffer,
        snapshotGroups: mixedSnapshots ? groups : undefined,
        quantityReviewRequired,
      });
    }
    rows.sort((a, b) => a.productName.localeCompare(b.productName));
    return rows;
  }

  /**
   * The newest offer comparable to the project snapshot (same price basis
   * preferred; offers scoped to another market rank below the sourcing
   * market's). Returns null unless a NEWER offer differs in price — and it
   * NEVER changes the snapshot itself.
   */
  private async currentOfferComparison(
    productId: string,
    snapshotCents: Cents,
    snapshotSource: string,
    snapshotObservedAt: string | null,
    requiredUnits: number,
    sourcingZip: string,
  ): Promise<OfferComparison | null> {
    const observations = await this.catalog.listObservations(productId);
    if (observations.length === 0) return null;
    const marketScore = (o: PriceObservation) =>
      o.marketZip === null || o.marketZip === "" ? 1 : o.marketZip === sourcingZip ? 2 : 0;
    const candidates = [...observations].sort((a, b) => {
      const src = Number(b.sourceType === snapshotSource) - Number(a.sourceType === snapshotSource);
      if (src !== 0) return src;
      const mk = marketScore(b) - marketScore(a);
      if (mk !== 0) return mk;
      return (b.observedAt + b.createdAt).localeCompare(a.observedAt + a.createdAt);
    });
    const current = candidates[0];
    if (!current) return null;
    if (snapshotObservedAt !== null && current.observedAt < snapshotObservedAt) return null;
    if (current.priceCents === snapshotCents) return null;
    const diff = current.priceCents - snapshotCents;
    return {
      observation: current,
      snapshotCents,
      diffPerUnitCents: diff,
      extendedDiffCents: diff * requiredUnits,
    };
  }

  /**
   * Candidate products for a requirement (spec §26A / PCPartPicker
   * direction): alternatives are products of the SAME material definition —
   * a deterministic domain relationship, never a guessed compatibility.
   */
  async alternativesFor(materialDefinitionId: string, requiredX100: number, currentProductId: string | null): Promise<ProductOption[]> {
    const products = (await this.catalog.listProductsForDefinition(materialDefinitionId)).filter(
      (p) => p.status !== "archived",
    );
    const options: ProductOption[] = [];
    for (const p of products) {
      const observations = await this.catalog.listObservations(p.id);
      const latest = observations[0] ?? null;
      const pq = computePurchaseQuantity(requiredX100, p.coveragePerUnitX100);
      options.push({
        product: p,
        latestOffer: latest,
        purchaseUnits: pq.purchaseUnits,
        purchasedCoverageX100: pq.purchaseUnits * p.coveragePerUnitX100,
        extendedCostCents: latest ? pq.purchaseUnits * latest.priceCents : null,
        isCurrentSelection: p.id === currentProductId,
      });
    }
    options.sort((a, b) => Number(b.isCurrentSelection) - Number(a.isCurrentSelection) || a.product.name.localeCompare(b.product.name));
    return options;
  }

  /** Offers for a product, sourcing-market matches first, then newest. */
  async offersForProduct(productId: string, sourcingZip: string): Promise<PriceObservation[]> {
    const observations = await this.catalog.listObservations(productId);
    const marketScore = (o: PriceObservation) =>
      o.marketZip === null || o.marketZip === "" ? 1 : o.marketZip === sourcingZip ? 2 : 0;
    return observations.sort(
      (a, b) => marketScore(b) - marketScore(a) || (b.observedAt + b.createdAt).localeCompare(a.observedAt + a.createdAt),
    );
  }

  /** Apply one offer/product to every member component of a requirement,
   *  then regenerate once. Explicit user action — never automatic. */
  async selectOfferForComponents(projectId: string, componentIds: string[], productId: string, observationId: string | null): Promise<void> {
    if (componentIds.length === 0) throw new ValidationError(["No requirement components to update."]);
    if (this.selectionHook) return this.selectionHook(projectId, componentIds, productId, observationId);
    await this.db.transaction(async () => {
      const ids = [...new Set(componentIds)];
      const members = await this.db.select<{ id: string }>(
        `SELECT c.id FROM assembly_instance_components c
         JOIN assembly_instances i ON i.id=c.instance_id
         JOIN project_features f ON f.id=i.project_feature_id
         WHERE f.project_id=? AND c.id IN (${ids.map(() => "?").join(",")})`, [projectId, ...ids]);
      if (members.length !== ids.length) throw new ValidationError(["These material requirements no longer belong to this project."]);
      for (const id of ids) {
        await this.catalog.selectProduct(id, productId, observationId, { allowDiscontinued: false });
      }
      await this.assemblies.regenerateForProject(projectId);
    });
  }

  /** Explicit Keep is a no-op by design; Update re-snapshots via the
   *  existing copy-on-use selection flow. */
  async updateSnapshotToOffer(projectId: string, componentIds: string[], observationId: string): Promise<void> {
    for (const id of componentIds) {
      await this.catalog.updateSelectionFromCatalog(id, observationId);
    }
    await this.assemblies.regenerateForProject(projectId);
  }

  /** Mark/unmark requirement components crew-supplied or not-required.
   *  Persisted intent only — never fabricates purchases or actual cost,
   *  and never mutates Design scope. */
  async setDisposition(componentIds: string[], disposition: ProcurementDisposition | null, note = ""): Promise<void> {
    const now = new Date().toISOString();
    await this.db.transaction(async () => {
      for (const id of componentIds) {
        if (disposition === null) {
          await this.db.execute("DELETE FROM procurement_dispositions WHERE component_id = ?", [id]);
        } else {
          await this.db.execute(
            `INSERT INTO procurement_dispositions (component_id, disposition, note, created_at, updated_at)
             VALUES (?, ?, ?, ?, ?)
             ON CONFLICT(component_id) DO UPDATE SET disposition = excluded.disposition, note = excluded.note, updated_at = excluded.updated_at`,
            [id, disposition, note, now, now],
          );
        }
      }
    });
  }

  /**
   * Record Purchase (spec §26C): one real Phase 5 Purchase → Line →
   * Allocation, product-linked, quantity recorded on BOTH line and
   * allocation so fulfillment attribution is first-class. Flows into
   * Actual Cost through the existing normalized event engine untouched.
   */
  async recordPurchase(input: RecordPurchaseInput): Promise<{ purchaseId: string }> {
    if (!Number.isSafeInteger(input.quantityX100) || input.quantityX100 <= 0) {
      throw new ValidationError(["Enter the quantity purchased."]);
    }
    const product = await this.catalog.getProduct(input.productId);
    if (!product) throw new ValidationError(["The product no longer exists."]);
    const netMerchandise = input.merchandiseCents - input.discountCents;
    if (netMerchandise < 0) throw new ValidationError(["Discount cannot exceed the merchandise amount."]);

    // Feature link only when the requirement belongs to exactly one feature.
    let featureId: string | null = null;
    if (input.componentIds.length > 0) {
      const placeholders = input.componentIds.map(() => "?").join(",");
      const rows = await this.db.select<{ project_feature_id: string }>(
        `SELECT DISTINCT i.project_feature_id
           FROM assembly_instance_components c JOIN assembly_instances i ON i.id = c.instance_id
          WHERE c.id IN (${placeholders})`,
        input.componentIds,
      );
      if (rows.length === 1) featureId = rows[0].project_feature_id;
    }

    let purchaseId = "";
    await this.db.transaction(async () => {
      const purchase = await this.purchases.createPurchase({
        kind: "purchase",
        originalPurchaseId: null,
        vendor: input.vendor,
        date: input.date,
        receiptNumber: input.receiptNumber,
        subtotalCents: input.merchandiseCents,
        discountCents: 0,
        taxCents: input.taxCents,
        shippingCents: input.shippingCents,
        totalCents: netMerchandise + input.taxCents + input.shippingCents,
        paymentMethod: "",
        refundMethod: null,
        notes: input.notes,
      });
      purchaseId = purchase.id;
      const line = await this.purchases.addLine(purchase.id, {
        description: product.name,
        quantityX100: input.quantityX100,
        unit: (input.unit || product.purchaseUnit) as QuantityUnit,
        sku: product.sku,
        grossCents: input.merchandiseCents,
        lineDiscountCents: input.discountCents,
        taxable: input.taxCents !== 0,
        explicitTaxCents: null,
        productId: product.id,
        categoryId: "materials",
        costType: "material",
        notes: "",
      });
      await this.purchases.addAllocation(line.id, {
        projectId: input.projectId,
        amountCents: netMerchandise,
        quantityX100: input.quantityX100,
        shippingOverrideCents: null,
        categoryOverrideId: null,
        bucketOverride: null,
        linkedProposedLineId: null,
        linkedFeatureId: featureId,
        notes: "Recorded from Procurement",
      });
    });
    return { purchaseId };
  }
}
